import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Newspaper, Users, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { FeedCard } from "@/components/feed/FeedCard";
import { PostComposer } from "@/components/profile/PostComposer";

// "Para ti" oculto até à Fase 3 (personalização); o backend continua a aceitar tab=for_you.
const TABS = [["recent", "feed_recent"], ["communities", "feed_communities"]];
const VALID = new Set(TABS.map(([id]) => id));

export default function Feed() {
  const { user } = useAuth();
  const { t } = useLang();
  const [tab, setTab] = useState(() => { const t = localStorage.getItem("feed_tab"); return VALID.has(t) ? t : "recent"; });
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const sentinel = useRef(null);
  useEffect(() => {
    const on = (e) => setItems((prev) => prev.filter((i) => (i.author?.id || i.author_id) !== e.detail?.userId));
    window.addEventListener("auk:user-blocked", on);
    return () => window.removeEventListener("auk:user-blocked", on);
  }, []);

  const load = useCallback(async (cur, reset = false) => {
    setBusy(true);
    try {
      const { data } = await api.get("/feed", { params: { tab, cursor: cur || undefined } });
      const cids = data.items.filter((i) => i.type === "community").map((i) => i.id);
      const pids = data.items.filter((i) => i.type === "profile").map((i) => i.id);
      const [mineC, mineP] = await Promise.all([
        cids.length ? api.get("/community-reactions/mine", { params: { post_ids: cids.join(",") } }).then((r) => r.data || {}).catch(() => ({})) : {},
        pids.length ? api.get("/profile-reactions/mine", { params: { post_ids: pids.join(",") } }).then((r) => r.data || {}).catch(() => ({})) : {},
      ]);
      const fresh = data.items.map((i) => ({ ...i, mine: (i.type === "community" ? mineC : mineP)[i.id] ?? null }));
      setItems((prev) => {
        const base = reset ? [] : prev;
        const seen = new Set(base.map((i) => `${i.type}:${i.id}`));
        return [...base, ...fresh.filter((i) => !seen.has(`${i.type}:${i.id}`))];
      });
      setCursor(data.next_cursor); setDone(!data.next_cursor);
    } catch { setDone(true); }
    finally { setBusy(false); }
  }, [tab]);

  useEffect(() => { localStorage.setItem("feed_tab", tab); setItems([]); setCursor(null); setDone(false); load(null, true); }, [tab, load]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el || done || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting) && cursor && !busy) load(cursor); }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [cursor, busy, done, load]);

  return (
    <div className="page fade-up" data-testid="feed-page">
      <div className="flex items-center justify-between py-3">
        <h1 className="font-display text-2xl font-extrabold flex items-center gap-2"><Newspaper size={22} /> {t("feed")}</h1>
        <Link to="/explorar" data-testid="feed-explore-link" className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-zinc-200 bg-white"><Compass size={13} /> {t("explore")}</Link>
      </div>
      {user && <div className="mb-3" data-testid="feed-composer"><PostComposer onPosted={() => { if (tab !== "recent") setTab("recent"); else { setItems([]); setCursor(null); setDone(false); load(null, true); } }} /></div>}
      <div className="flex gap-2 mb-3 overflow-x-auto scrollbar-none" data-testid="feed-tabs">
        {TABS.map(([id, key]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`feed-tab-${id}`} data-active={tab === id}
                  className={`chip whitespace-nowrap inline-flex items-center gap-1 ${tab === id ? "bg-black text-white" : ""}`}>
            {id === "communities" ? <Users size={12} /> : <Newspaper size={12} />} {t(key)}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {items.map((it) => <FeedCard key={`${it.type}:${it.id}`} item={it} user={user} onRemoved={(x) => setItems((prev) => prev.filter((i) => !(i.type === x.type && i.id === x.id)))} />)}
      </div>
      {!busy && items.length === 0 && done && (
        <div className="text-center text-sm text-zinc-500 py-12" data-testid="feed-empty">{t("feed_empty")}</div>
      )}
      <div ref={sentinel} className="h-8" />
      {busy && <div className="flex justify-center py-4" data-testid="feed-loading"><Loader2 size={18} className="animate-spin text-zinc-400" /></div>}
      {!busy && cursor && <button onClick={() => load(cursor)} data-testid="feed-more" className="mx-auto block text-xs font-semibold px-4 py-2 rounded-full bg-white border border-zinc-200 mb-4">{t("feed_more")}</button>}
      {done && items.length > 0 && <p className="text-center text-xs text-zinc-400 py-4" data-testid="feed-end">{t("feed_end")}</p>}
    </div>
  );
}
